import React, { useState, useEffect, useContext } from "react"
import { CALENDAR_MONTHS } from "../../utils/exports/months"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./segregated-conversions.scss"

// UI imports
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Table from "../../components/structure/table/table"
import Button from "../../components/ui/button/button"
import { LoadingIcon, InformationIcon } from "../../utils/svgs"

// Context imports
import { ModalContext } from "../../utils/providers/modal"

// Build up a list of years for the select field
let yearsObj = {}
let nextYear = Number(moment().startOf("year").add(2, "years").format("YYYY"))
for (let i = nextYear; i >= 2018; i--) {
	yearsObj = {
		[i]: i,
		...yearsObj,
	}
}

// Returns the HTML markup for the conversions page
export default function SegregatedConversions() {
	const [loading, setLoading] = useState(false)
	const [agents, setAgents] = useState([])

	const [month, setMonth] = useState("")
	const [year, setYear] = useState("")

	const [agentRows, setAgentRows] = useState([])
	const [siteRows, setSiteRows] = useState([])
	const [clientHistoryMap, setClientHistoryMap] = useState(null)
	const [rowLoadingStates, setRowLoadingStates] = useState({})
	const [siteRowLoadingStates, setSiteRowLoadingStates] = useState({})
	const [enquiriesData, setEnquiriesData] = useState([])
	const [bookingsData, setBookingsData] = useState([])

	// Get modal context
	const { showModal } = useContext(ModalContext)

	// On component load
	useEffect(() => {
		const fetchSalesAgents = () => {
			db.collection("users")
				.where("show_for.targets", "==", true)
				.orderBy("first_name")
				.get()
				.then((agentDocs) => {
					const agents = agentDocs.docs.map((doc) => {
						return {
							id: doc.id,
							name: `${doc.data().first_name} ${doc.data().last_name}`,
						}
					})
					setAgents(agents)
				})
		}
		fetchSalesAgents()
	}, [])

	// Build a memory-efficient map of client booking history
	const buildClientHistoryMap = async () => {
		try {
			// Fetch all bookings (use created, since DIRECT bookings don't have enquiry.created)
			const allBookings = await db.collection("bookings").orderBy("created").get()

			// Create a map of client ID to earliest booking timestamp
			const historyMap = new Map()

			allBookings.docs.forEach((doc) => {
				const data = doc.data()
				const client = data.client
				// Prefer enquiry.created (enquiry-based bookings), fallback to booking.created (DIRECT)
				const createdSeconds = data.enquiry?.created?.seconds ?? data.created?.seconds

				// Skip if missing data
				if (!client || !createdSeconds) return

				// Only keep the earliest booking for each client
				if (!historyMap.has(client) || historyMap.get(client) > createdSeconds) {
					historyMap.set(client, createdSeconds)
				}
			})

			return historyMap
		} catch (err) {
			console.error("Error building client history map:", err)
			return new Map()
		}
	}

	// Process a single row (agent or site) to calculate new/repeat conversion rates
	const processGroupRow = (group, enquiries, bookings, historyMap, filterKey) => {
		// Get enquiries and bookings for this group (filter by agent or site)
		const groupEnquiries = enquiries.filter((enq) => enq[filterKey] === group.id)
		const groupBookings = bookings.filter((booking) => booking[filterKey] === group.id)

		// Categorize enquiries as new or repeat
		let newEnquiries = []
		let repeatEnquiries = []

		groupEnquiries.forEach((enquiry) => {
			const clientHistoryTimestamp = historyMap.get(enquiry.client)
			const enquiryTimestamp = moment(enquiry.created).unix()

			// Check if client had a previous booking before this enquiry
			const isRepeat = clientHistoryTimestamp && clientHistoryTimestamp < enquiryTimestamp

			if (isRepeat) {
				repeatEnquiries.push(enquiry)
			} else {
				newEnquiries.push(enquiry)
			}
		})

		// Categorize bookings as new or repeat
		let newBookings = []
		let repeatBookings = []

		groupBookings.forEach((booking) => {
			const clientHistoryTimestamp = historyMap.get(booking.client)
			const bookingEnquiryTimestamp = moment(booking.created).unix()

			// Check if client had a previous booking before this booking's enquiry was created
			const isRepeat = clientHistoryTimestamp && clientHistoryTimestamp < bookingEnquiryTimestamp

			if (isRepeat) {
				repeatBookings.push(booking)
			} else {
				newBookings.push(booking)
			}
		})

		// Calculate conversion rates
		const newConversionRate = newEnquiries.length > 0 ? ((newBookings.length / newEnquiries.length) * 100).toFixed(2) : "N/A"

		const repeatConversionRate = repeatEnquiries.length > 0 ? ((repeatBookings.length / repeatEnquiries.length) * 100).toFixed(2) : "N/A"

		return {
			newConversion: newConversionRate,
			repeatConversion: repeatConversionRate,
			newEnquiriesCount: newEnquiries.length,
			repeatEnquiriesCount: repeatEnquiries.length,
			newBookingsCount: newBookings.length,
			repeatBookingsCount: repeatBookings.length,
		}
	}

	// Fetch the conversion data
	const fetchConversionsData = async () => {
		if (!month || !year) return

		try {
			// Set loading to true
			setLoading(true)

			// Reset the state
			setSiteRows([])
			setAgentRows([])
			setClientHistoryMap(null)
			setRowLoadingStates({})
			setSiteRowLoadingStates({})
			setEnquiriesData([])
			setBookingsData([])

			// Setup some empty date objects
			let windowStart = null
			let windowEnd = null

			if (month && year) {
				// If we have both the year and the month
				windowStart = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
				windowEnd = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()
			} else if (!month && year) {
				// If we only have the year
				windowStart = moment(`${year}`, "YYYY").startOf("year").valueOf()
				windowEnd = moment(`${year}`, "YYYY").endOf("year").valueOf()
			}

			// Convert the moment objects into firestore timestamps
			const startTimestamp = firebase.firestore.Timestamp.fromMillis(windowStart)
			const endTimestamp = firebase.firestore.Timestamp.fromMillis(windowEnd)

			// Shared test accounts filter
			const testAccounts = ["apH2Vpa4qns0YTnelDMX", "UPyzRTUdKGlJgiCCxseP", "RxnT7D5WGjPddb9dSCp4"]

			// Fetch all the open enquiries (exclude archived from stats)
			const openEnquiries = await db
				.collection("enquiries")
				.where("created", ">=", startTimestamp)
				.where("created", "<=", endTimestamp)
				.get()
				.then((enquiryDocs) => {
					return enquiryDocs.docs
						.filter((doc) => doc.data().archived_from_stats !== true)
						.map((doc) => {
							const createdTs = moment(doc.data().created.seconds, "X").valueOf()

							return {
								created: createdTs,
								agent: doc.data().agent,
								client: doc.data().client,
								location: doc.data().location,
								site: doc.data().site,
							}
						})
				})

			// Fetch all the closed enquiries (exclude archived from stats)
			const closedEnquiries = await db
				.collection("enquiries_removed")
				.where("enquiry.created", ">=", startTimestamp)
				.where("enquiry.created", "<=", endTimestamp)
				.get()
				.then((enquiryDocs) => {
					return enquiryDocs.docs
						.filter((doc) => doc.data().archived_from_stats !== true)
						.map((doc) => {
							const createdTs = moment(doc.data().enquiry.created.seconds, "X").valueOf()

							return {
								created: createdTs,
								client: doc.data().enquiry.client,
								location: doc.data().enquiry.location,
								agent: doc.data().enquiry.agent,
								site: doc.data().enquiry.site,
							}
						})
				})

			// Combine them into a single array
			const combinedEnquiries = [...openEnquiries, ...closedEnquiries]

			// Remove enquiries from test accounts
			const finalEnquiryList = combinedEnquiries.filter((enquiry) => !testAccounts.includes(enquiry.client))

			// Fetch enquiry-based bookings (attributed by enquiry.created)
			const enquiryBookings = await db
				.collection("bookings")
				.where("enquiry.created", ">=", startTimestamp)
				.where("enquiry.created", "<=", endTimestamp)
				.get()
				.then((bookingDocs) => {
					const bookingsWithFields = bookingDocs.docs.map((doc) => {
						const data = doc.data()
						const { created, location, site } = data.enquiry || {}
						const createdTs = moment(created?.seconds, "X").valueOf()

						return {
							id: doc.id,
							created: createdTs,
							enquiry_created: moment(created?.seconds, "X").format("DD-MM-YYYY"),
							booked: moment(data.booked?.seconds, "X").format("DD-MM-YYYY"),
							booked_location: data.booked_location,
							client: data.client,
							agent: data.agent,
							location,
							site,
						}
					})

					return bookingsWithFields.filter((booking) => !isNaN(booking.created) && booking.client && !testAccounts.includes(booking.client))
				})

			// Fetch DIRECT bookings (attributed by booking.created)
			const directBookings = await db
				.collection("bookings")
				.where("created", ">=", startTimestamp)
				.where("created", "<=", endTimestamp)
				.get()
				.then((bookingDocs) => {
					const bookingsWithFields = bookingDocs.docs
						.map((doc) => {
							const data = doc.data()
							if (data.booking_type !== "DIRECT") return null

							const createdTs = moment(data.created?.seconds, "X").valueOf()

							return {
								id: doc.id,
								created: createdTs,
								enquiry_created: moment(data.created?.seconds, "X").format("DD-MM-YYYY"),
								booked: moment(data.booked?.seconds, "X").format("DD-MM-YYYY"),
								booked_location: data.booked_location,
								client: data.client,
								agent: data.agent,
								location: data.enquiry?.location,
								site: data.enquiry?.site,
							}
						})
						.filter(Boolean)

					return bookingsWithFields.filter((booking) => !isNaN(booking.created) && booking.client && !testAccounts.includes(booking.client))
				})

			// Combine both booking sets
			const bookings = [...enquiryBookings, ...directBookings]

			// Map over each enquiry and find the relevant booking for it
			finalEnquiryList.forEach((enquiry) => {
				const booking = bookings.filter((booking) => booking.client === enquiry.client && booking.location === enquiry.location && booking.created === enquiry.created)
				if (booking.length > 1) {
					console.log(enquiry.client, enquiry.location, enquiry.created)
				}
				if (booking.length > 0) enquiry.booked = true
			})

			// Find all the booked enquiries
			const bookedEnquiries = finalEnquiryList.filter((enquiry) => enquiry.booked)

			// Make an array of enquiries with no booking
			const noBookingEnquiries = finalEnquiryList.filter((enquiry) => !enquiry.booked)

			// Map over these enquiries and check to see if there is one with the same client ID in the bookings array
			const filteredEnquiries = []
			noBookingEnquiries.forEach((enquiry) => {
				const booking = bookings.find((booking) => booking.client === enquiry.client)
				if (!booking) filteredEnquiries.push(enquiry)
			})

			// Make sure there are no duplicate enquiries
			const uniqueEnquiries = filteredEnquiries.filter((enquiry, index, self) => self.findIndex((t) => t.client === enquiry.client) === index)

			// Combine the final enquiries that are de-duped and booked
			const finalEnquiries = [...uniqueEnquiries, ...bookedEnquiries]

			// Map over each of the agents
			agents.forEach((agent) => {
				// Find all the enquiries and bookings for this agent
				const agentEnquiries = finalEnquiries.filter((enquiry) => enquiry.agent === agent.id)
				const agentBookings = bookings.filter((booking) => booking.agent === agent.id)

				agent.enquiries = agentEnquiries.length
				agent.bookings = agentBookings.length
				agent.conversion = ((agentBookings.length / agentEnquiries.length) * 100).toFixed(2)
			})

			// Store enquiries and bookings data for row processing
			setEnquiriesData(finalEnquiries)
			setBookingsData(bookings)

			// Build the client history map
			const historyMap = await buildClientHistoryMap()
			setClientHistoryMap(historyMap)

			// Create site rows from unique sites in enquiries
			const uniqueSites = [...new Set(finalEnquiries.map((enq) => enq.site).filter(Boolean))]
			const sites = uniqueSites.map((site) => {
				// Find all the enquiries and bookings for this site
				const siteEnquiries = finalEnquiries.filter((enquiry) => enquiry.site === site)
				const siteBookings = bookings.filter((booking) => booking.site === site)

				return {
					id: site,
					name: site,
					enquiries: siteEnquiries.length,
					bookings: siteBookings.length,
					conversion: ((siteBookings.length / siteEnquiries.length) * 100).toFixed(2),
				}
			})

			// Set site and agent rows (this will trigger the useEffects to process rows)
			setSiteRows(sites)
			setAgentRows(agents)
		} catch (err) {
			console.log(err)
		} finally {
			setLoading(false)
		}
	}

	// Process site rows sequentially after data is loaded
	useEffect(() => {
		// Only process if we have all the data we need
		if (!siteRows.length || !clientHistoryMap || !enquiriesData.length) return

		// Process each site row sequentially
		const processSiteRows = async () => {
			// Initialize loading states for all rows
			const initialLoadingStates = {}
			siteRows.forEach((site) => {
				initialLoadingStates[site.id] = true
			})
			setSiteRowLoadingStates(initialLoadingStates)

			// Process each row with a small delay to show progressive loading
			for (let i = 0; i < siteRows.length; i++) {
				const site = siteRows[i]

				// Calculate new/repeat conversion rates
				const rowData = processGroupRow(site, enquiriesData, bookingsData, clientHistoryMap, "site")

				// Update the site row with the calculated data
				setSiteRows((prevRows) => {
					const newRows = [...prevRows]
					newRows[i] = {
						...newRows[i],
						...rowData,
					}
					return newRows
				})

				// Mark this row as no longer loading
				setSiteRowLoadingStates((prev) => ({
					...prev,
					[site.id]: false,
				}))
			}
		}

		processSiteRows()
	}, [siteRows.length, clientHistoryMap, enquiriesData.length])

	// Process agent rows sequentially after data is loaded
	useEffect(() => {
		// Only process if we have all the data we need
		if (!agentRows.length || !clientHistoryMap || !enquiriesData.length) return

		// Process each agent row sequentially
		const processAgentRows = async () => {
			// Initialize loading states for all rows
			const initialLoadingStates = {}
			agentRows.forEach((agent) => {
				initialLoadingStates[agent.id] = true
			})
			setRowLoadingStates(initialLoadingStates)

			// Process each row with a small delay to show progressive loading
			for (let i = 0; i < agentRows.length; i++) {
				const agent = agentRows[i]

				// Calculate new/repeat conversion rates
				const rowData = processGroupRow(agent, enquiriesData, bookingsData, clientHistoryMap, "agent")

				// Update the agent row with the calculated data
				setAgentRows((prevRows) => {
					const newRows = [...prevRows]
					newRows[i] = {
						...newRows[i],
						...rowData,
					}
					return newRows
				})

				// Mark this row as no longer loading
				setRowLoadingStates((prev) => ({
					...prev,
					[agent.id]: false,
				}))

				// Small delay to allow UI to update (optional, can be removed for faster processing)
				await new Promise((resolve) => setTimeout(resolve, 10))
			}
		}

		processAgentRows()
	}, [agentRows.length, clientHistoryMap, enquiriesData.length])

	// Calculate overall totals for site table
	const calculateSiteOveralls = () => {
		if (!siteRows.length) return null

		// Check if all rows have loaded their new/repeat data
		const allRowsLoaded = siteRows.every((row) => row.newConversion !== undefined)
		if (!allRowsLoaded) return null

		// Sum together all the rows for totals
		const totals = siteRows.reduce(
			(acc, row) => {
				acc.enquiries += row.enquiries || 0
				acc.bookings += row.bookings || 0
				acc.newEnquiries += row.newEnquiriesCount || 0
				acc.newBookings += row.newBookingsCount || 0
				acc.repeatEnquiries += row.repeatEnquiriesCount || 0
				acc.repeatBookings += row.repeatBookingsCount || 0
				return acc
			},
			{
				enquiries: 0,
				bookings: 0,
				newEnquiries: 0,
				newBookings: 0,
				repeatEnquiries: 0,
				repeatBookings: 0,
			}
		)

		// Calculate conversion rates
		const overallConversion = totals.enquiries > 0 ? ((totals.bookings / totals.enquiries) * 100).toFixed(2) : "N/A"
		const newConversion = totals.newEnquiries > 0 ? ((totals.newBookings / totals.newEnquiries) * 100).toFixed(2) : "N/A"
		const repeatConversion = totals.repeatEnquiries > 0 ? ((totals.repeatBookings / totals.repeatEnquiries) * 100).toFixed(2) : "N/A"

		return {
			...totals,
			overallConversion,
			newConversion,
			repeatConversion,
		}
	}

	// Calculate overall totals for site table
	const siteOveralls = calculateSiteOveralls()

	// Show information modal explaining calculations
	const showInfoModal = () => {
		showModal({
			title: "How Calculations Work",
			body: (
				<div className="info-modal-content">
					<h4>Total Enquiries (De-Duped):</h4>
					<ul>
						<li>Enquiries are de-duplicated by client - if the same client enquired multiple times in the period, they're only counted once</li>
						<li>Both open and closed enquiries within the selected date range are included</li>
					</ul>

					<h4>Total Bookings:</h4>
					<ul>
						<li>Count of all bookings where the original enquiry was created within the selected period</li>
					</ul>

					<h4>Conversion Rate (Combined):</h4>
					<ul>
						<li>
							<strong>Formula:</strong> (Total Bookings ÷ Total Enquiries) × 100
						</li>
						<li>
							<strong>Example:</strong> 10 bookings ÷ 80 enquiries = 12.50%
						</li>
					</ul>

					<h4>New vs Repeat Client Classification:</h4>
					<p>A client is classified as "new" or "repeat" based on their booking history at the time they made the enquiry:</p>
					<ul>
						<li>
							<strong>NEW CLIENT:</strong> Had never booked with us before when they enquired
						</li>
						<li>
							<strong>REPEAT CLIENT:</strong> Had at least one previous booking before this enquiry
						</li>
					</ul>
					<p className="example-text">
						<em>Example: If Client A enquired on Oct 10, 2024, but had previously booked from an enquiry on Jan 15, 2023, this Oct enquiry is counted as a REPEAT enquiry.</em>
					</p>

					<h4>Conversion Rates (New/Repeat):</h4>
					<ul>
						<li>
							<strong>New Business:</strong> (New Bookings ÷ New Enquiries) × 100
						</li>
						<li>
							<strong>Repeats:</strong> (Repeat Bookings ÷ Repeat Enquiries) × 100
						</li>
					</ul>

					<h4>Example Breakdown:</h4>
					<p>Agent has 72 total enquiries and 6 bookings:</p>
					<ul>
						<li>
							59 enquiries from new clients → 6 converted = <strong>10.17%</strong>
						</li>
						<li>
							13 enquiries from repeat clients → 0 converted = <strong>0.00%</strong>
						</li>
						<li>
							Combined: 6 bookings ÷ 72 enquiries = <strong>8.33%</strong>
						</li>
					</ul>
					<p className="note-text">The combined rate is lower because those 13 repeat enquiries that didn't convert pull down the overall average.</p>
				</div>
			),
			cancel: {
				label: "Close",
				action: () => {},
			},
		})
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Conversions</h1>
			</Title>

			<div className="page-filters extra-stats">
				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={yearsObj}
				/>

				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button
						filters={true}
						disabled={(!month || !year) && !year}
						label="Fetch"
						loadingText="Fetching..."
						loading={loading}
						onClick={() => fetchConversionsData()}
					/>
					<div
						className="info-icon-wrapper"
						onClick={showInfoModal}
						title="How are these calculations made?">
						<InformationIcon />
					</div>
				</div>
			</div>

			{siteRows.length > 0 && (
				<>
					<Table
						className="conversions-table"
						headings={[
							"Site",
							"Total Enquiries",
							"Total Bookings",
							"Conv. Rate (Combined)",
							"New Enquiries",
							"New Bookings",
							"Conv. Rate (New Business)",
							"Repeat Enquiries",
							"Repeat Bookings",
							"Conv. Rate (Repeats)",
						]}
						noResults={!siteRows.length}
						noResultsMessage="No site data available">
						{siteRows.map((siteRow) => (
							<tr key={siteRow.name}>
								<td>{siteRow.name}</td>
								<td className="group-separator">{siteRow.enquiries}</td>
								<td>{siteRow.bookings}</td>
								<td>{siteRow.conversion}%</td>
								<td className="group-separator">
									{siteRowLoadingStates[siteRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : siteRow.newEnquiriesCount !== undefined ? (
										siteRow.newEnquiriesCount
									) : (
										""
									)}
								</td>
								<td>
									{siteRowLoadingStates[siteRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : siteRow.newBookingsCount !== undefined ? (
										siteRow.newBookingsCount
									) : (
										""
									)}
								</td>
								<td>
									{siteRowLoadingStates[siteRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : siteRow.newConversion !== undefined ? (
										siteRow.newConversion === "N/A" ? (
											"N/A"
										) : (
											`${siteRow.newConversion}%`
										)
									) : (
										""
									)}
								</td>
								<td className="group-separator">
									{siteRowLoadingStates[siteRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : siteRow.repeatEnquiriesCount !== undefined ? (
										siteRow.repeatEnquiriesCount
									) : (
										""
									)}
								</td>
								<td>
									{siteRowLoadingStates[siteRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : siteRow.repeatBookingsCount !== undefined ? (
										siteRow.repeatBookingsCount
									) : (
										""
									)}
								</td>
								<td>
									{siteRowLoadingStates[siteRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : siteRow.repeatConversion !== undefined ? (
										siteRow.repeatConversion === "N/A" ? (
											"N/A"
										) : (
											`${siteRow.repeatConversion}%`
										)
									) : (
										""
									)}
								</td>
							</tr>
						))}
						{siteOveralls && (
							<tr className="overalls-row">
								<td>
									<strong>Overall Avg.</strong>
								</td>
								<td className="group-separator">
									<strong>{siteOveralls.enquiries}</strong>
								</td>
								<td>
									<strong>{siteOveralls.bookings}</strong>
								</td>
								<td>
									<strong>{siteOveralls.overallConversion === "N/A" ? "N/A" : `${siteOveralls.overallConversion}%`}</strong>
								</td>
								<td className="group-separator">
									<strong>{siteOveralls.newEnquiries}</strong>
								</td>
								<td>
									<strong>{siteOveralls.newBookings}</strong>
								</td>
								<td>
									<strong>{siteOveralls.newConversion === "N/A" ? "N/A" : `${siteOveralls.newConversion}%`}</strong>
								</td>
								<td className="group-separator">
									<strong>{siteOveralls.repeatEnquiries}</strong>
								</td>
								<td>
									<strong>{siteOveralls.repeatBookings}</strong>
								</td>
								<td>
									<strong>{siteOveralls.repeatConversion === "N/A" ? "N/A" : `${siteOveralls.repeatConversion}%`}</strong>
								</td>
							</tr>
						)}
					</Table>
				</>
			)}

			<br />
			<br />

			{agentRows.length > 0 && (
				<>
					<Table
						className="conversions-table"
						headings={[
							"Name",
							"Total Enquiries",
							"Total Bookings",
							"Conv. Rate (Combined)",
							"New Enquiries",
							"New Bookings",
							"Conv. Rate (New Business)",
							"Repeat Enquiries",
							"Repeat Bookings",
							"Conv. Rate (Repeats)",
						]}
						noResults={!agentRows.length}
						noResultsMessage="Choose some parameters to fetch conversion data">
						{agentRows.map((agentRow) => (
							<tr key={agentRow.name}>
								<td>{agentRow.name}</td>
								<td className="group-separator">{agentRow.enquiries}</td>
								<td>{agentRow.bookings}</td>
								<td>{agentRow.conversion}%</td>
								<td className="group-separator">
									{rowLoadingStates[agentRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : agentRow.newEnquiriesCount !== undefined ? (
										agentRow.newEnquiriesCount
									) : (
										""
									)}
								</td>
								<td>
									{rowLoadingStates[agentRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : agentRow.newBookingsCount !== undefined ? (
										agentRow.newBookingsCount
									) : (
										""
									)}
								</td>
								<td>
									{rowLoadingStates[agentRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : agentRow.newConversion !== undefined ? (
										agentRow.newConversion === "N/A" ? (
											"N/A"
										) : (
											`${agentRow.newConversion}%`
										)
									) : (
										""
									)}
								</td>
								<td className="group-separator">
									{rowLoadingStates[agentRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : agentRow.repeatEnquiriesCount !== undefined ? (
										agentRow.repeatEnquiriesCount
									) : (
										""
									)}
								</td>
								<td>
									{rowLoadingStates[agentRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : agentRow.repeatBookingsCount !== undefined ? (
										agentRow.repeatBookingsCount
									) : (
										""
									)}
								</td>
								<td>
									{rowLoadingStates[agentRow.id] ? (
										<div className="cell-loading-spinner">
											<LoadingIcon />
										</div>
									) : agentRow.repeatConversion !== undefined ? (
										agentRow.repeatConversion === "N/A" ? (
											"N/A"
										) : (
											`${agentRow.repeatConversion}%`
										)
									) : (
										""
									)}
								</td>
							</tr>
						))}
					</Table>
				</>
			)}
		</Tile>
	)
}
